import { Brand, Tag } from '@prisma/client';
import { json, type LoaderFunction, type MetaFunction } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { Container } from '~/components/Container';

import { Layout } from '~/components/Layout';
import { PageSubHeading } from '~/components/PageSubHeading';
import { PedalList } from '~/components/PedalList';
import { ReadMoreButton } from '~/components/ReadMoreButton';
import { TagLink } from '~/components/TagLink';
import { db } from '~/lib/db.server';
import { getCacheControlHeader } from '~/lib/http.server';
import { getImagePublicUrl } from '~/lib/supabase.server';
import { appendMetaTitleSuffix } from '~/lib/utils/meta';
import { Hero } from '~/routes/_index/Hero';

export const loader: LoaderFunction = async ({ request }) => {

  const [latestPedals, tags, pickupBrands] = await Promise.all([
    db.pedal.findMany({
      take: 18,
      include: {
        brand: true,
        images: true,
        tags: {
          include: {
            tag: true,
          },
        },
      },
      orderBy: {
        createdAt: 'desc',
      },
    }),
    db.tag.findMany({
      take: 20,
      orderBy: {
        order: 'asc',
      },
    }),
    db.brand.findMany({
      take: 10,
      where: {
        isPickup: true,
      },
      orderBy: {
        order: 'asc',
      },
    }),
  ]);

  const imageUrlPrefix = getImagePublicUrl(request);

  return json(
    {
      latestPedals,
      tags,
      pickupBrands,
      imageUrlPrefix,
    },
    {
      headers: {
        ...getCacheControlHeader()
      }
    }
  );
};

export const meta: MetaFunction = () => {
  return [
    { title: appendMetaTitleSuffix('ざっくり検索で欲しいエフェクターが見つかるデータベース') },
    { name: 'description', content: 'StompBase（ストンプベース）はキーワードやタグ・ブランドでざっくりと検索して欲しいエフェクターが見つかるデータベースサービスです。' },
  ];
};

const IndexRoute = () => {
  const { latestPedals, tags, pickupBrands, imageUrlPrefix } = useLoaderData<typeof loader>();

  return (
    <Layout>
      <div className="flex flex-col items-center">
        {/* <div className={'absolute w-[100%] pt-9 md:pt-12 pb-8 md:pb-12 flex items-center justify-center'} style={{ background: 'url(/hero.jpg)', backgroundSize: '630px auto' }}> */}
        {/* <div className={'absolute w-[100%] h-[310px] md:h-[490px] flex items-center justify-center'} style={{ background: 'url(/hero.jpg)', backgroundSize: '630px auto' }}> */}
        <div className="w-[100%] pt-1 md:pt-2 pb-8 md:pb-16 flex items-center justify-center" style={{ background: 'url(/hero.jpg)', backgroundSize: '630px auto' }}>
          <Hero />
        </div>

        <div className="w-full mt-1">
          <Container>
            <div className={'mt-6'}>
              <PageSubHeading>
                新着エフェクター
              </PageSubHeading>
              {latestPedals.length > 0 && (
                <>
                  <div className={'grid lg:grid-cols-3 md:grid-cols-1 gap-6 mt-3'}>
                    <PedalList pedals={latestPedals} imageUrlPrefix={imageUrlPrefix} />
                  </div>

                  <div className='mt-10'>
                    <ReadMoreButton to={`/pedals`} label={`エフェクターをもっとみる`} />
                  </div>
                </>
              )}
            </div>

            <div className={'mt-14'}>
              <PageSubHeading>
                タグで探す
              </PageSubHeading>
              {tags.length > 0 && (
                <>
                  <div className={'flex flex-wrap gap-4 mt-3'}>
                    {tags.map((tag: Tag) => (
                        <div key={tag.id} className="flex-shrink-0">
                          <TagLink id={tag.id} tag={tag.tag} pedalCount={tag.pedalCount} />
                        </div>
                      ))}
                  </div>

                  <div className='mt-10'>
                    <ReadMoreButton to={`/tags`} label={`タグを全部みる`} />
                  </div>
                </>
              )}
            </div>

            <div className={'mt-14'}>
              <PageSubHeading>
                注目のブランド
              </PageSubHeading>
              {pickupBrands.length > 0 && (
                <>
                  <ul className={'grid grid-cols-1 md:grid-cols-4 gap-4 mt-4'}>
                    {pickupBrands
                      .map((brand: Brand) => (
                        <li key={brand.id}>
                          <Link to={`/brands/${brand.id}`} className={'text-gray-700 font-bold hover:underline hover:text-indigo-400 flex items-center'}>
                            {brand.name}
                            <span className='ml-[6px] mt-[1px] text-slate-400 text-xs font-normal'>
                              (<span className='mx-[2px]'>{brand.pedalCount}</span>)
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ul>

                  <div className='mt-10'>
                    <ReadMoreButton to={`/brands`} label={`ブランドを全部みる`} />
                  </div>
                </>
              )}
            </div>
          </Container>

        </div>

      </div>
    </Layout>
  );
}

export default IndexRoute;